import {
  POS_LINE_KIND_LABELS,
  POS_SALE_KIND_LABELS,
  POS_SALE_SOURCE_LABELS,
  type PosSaleDetail,
  type PosSaleLine,
  type PosSaleTender,
} from "@station/contracts";
import { Decimal } from "@station/core";
import { Alert, Card, MoneyText, PageHeader, SkeletonRows, StatusBadge, type Tone } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import {
  Field,
  ModuleDisabled,
  PosSaleStatusBadge,
  tenderLabel,
  warningLabel,
} from "../../../../features/pos/shared.js";
import { usePosPermissions, usePosSale } from "../../../../features/pos/queries.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatMinor } from "../../../../features/shifts/shift-values.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../../lib/format.js";
import { useDocumentTitle } from "../../../../lib/hooks.js";

export const Route = createFileRoute("/_app/kasa/satislar/$id")({
  component: PosSaleDetailRoute,
});

/**
 * Kasa fişi ayrıntısı (Prompt 15).
 *
 * Fiş DEĞİŞTİRİLMEZ ve silinmez (AGENTS.md §3.5): iptal / iade ayrı bir kayıt olarak doğar ve iki kayıt
 * `voidOfSaleId` / `voidedBySaleId` ile birbirine bağlanır. Geçersiz kılınmış ödeme satırı (ödeme
 * düzeltmesi) SİLİNMEZ, üstü çizili gösterilir ve gerekçesi yazılır.
 */
function PosSaleDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = usePosPermissions();
  const sale = usePosSale(id, permissions.enabled && permissions.canRead);
  const detail = sale.data as PosSaleDetail | undefined;
  useDocumentTitle(detail ? receiptText(detail) : t("menu.posSales"));

  return (
    <>
      <PageHeader
        title={detail ? receiptText(detail) : t("pos.sales.detailTitle")}
        description={t("pos.sales.detailDescription")}
        breadcrumb={[
          { label: t("menu.groups.devices") },
          { label: t("pos.groupLabel") },
          {
            label: t("menu.posSales"),
            link: (children, className) => (
              <Link to="/kasa/satislar" className={className}>
                {children}
              </Link>
            ),
          },
          { label: detail ? receiptText(detail) : t("pos.sales.detailTitle") },
        ]}
      />
      {!permissions.enabled ? (
        <ModuleDisabled />
      ) : !permissions.canRead ? (
        <Alert tone="info">{t("pos.noPermission")}</Alert>
      ) : sale.error ? (
        <Alert tone="danger">{errorMessage(sale.error)}</Alert>
      ) : !detail ? (
        <SkeletonRows rows={8} />
      ) : (
        <div className="grid gap-4">
          <SaleHeader sale={detail} />
          <LinesCard sale={detail} />
          <TendersCard sale={detail} />
          <WarningsCard sale={detail} />
        </div>
      )}
    </>
  );
}

function receiptText(sale: PosSaleDetail): string {
  if (sale.receiptNo === null && sale.fiscalDeviceSerial === null) return sale.agentSaleId;
  return [sale.fiscalDeviceSerial, sale.zNo === null ? null : `Z${sale.zNo}`, sale.receiptNo]
    .filter((part) => part !== null && part !== undefined)
    .join(" · ");
}

function SaleHeader({ sale }: { sale: PosSaleDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Card className="grid gap-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <PosSaleStatusBadge status={sale.status} />
        <StatusBadge tone={sale.kind === "RETURN" ? "warning" : "neutral"}>
          {POS_SALE_KIND_LABELS[sale.kind]}
        </StatusBadge>
        <StatusBadge tone="neutral">{POS_SALE_SOURCE_LABELS[sale.source]}</StatusBadge>
        {sale.afterDayLock ? <StatusBadge tone="danger">{t("pos.sales.afterDayLock")}</StatusBadge> : null}
        {sale.ageConfirmedAt ? <StatusBadge tone="info">{t("pos.sales.ageConfirmed")}</StatusBadge> : null}
      </div>

      {sale.afterDayLock ? <Alert tone="warning">{t("pos.sales.afterDayLockHint")}</Alert> : null}
      {sale.voidReason ? (
        <Alert tone="warning" title={t("pos.sales.voidReason")}>
          {sale.voidReason}
        </Alert>
      ) : null}

      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("pos.sales.fields.occurredAt")}>{formatDateTime(sale.occurredAt)}</Field>
        <Field label={t("pos.sales.fields.businessDate")}>{formatDate(sale.businessDate)}</Field>
        <Field label={t("pos.fields.station")}>{sale.stationName ?? "—"}</Field>
        <Field label={t("pos.sales.fields.staff")}>{sale.staffName ?? "—"}</Field>
        <Field label={t("pos.sales.fields.cashRegister")}>{sale.cashRegisterName ?? "—"}</Field>
        <Field label={t("pos.sales.fields.agentSaleId")}>
          <span className="font-mono text-xs">{sale.agentSaleId}</span>
        </Field>
        <Field label={t("pos.sales.fields.counterparty")}>{sale.counterpartyName ?? "—"}</Field>
        <Field label={t("pos.sales.fields.approvedBy")}>{sale.approvedByStaffName ?? "—"}</Field>
        <Field label={t("pos.sales.fields.shift")}>
          {sale.shiftId === null ? (
            "—"
          ) : (
            <Link
              to="/vardiya/vardiyalar/$id"
              params={{ id: sale.shiftId }}
              className="underline underline-offset-4"
            >
              {sale.shiftNo ?? t("pos.sales.openShift")}
            </Link>
          )}
        </Field>
        <Field label={t("pos.sales.fields.closing")}>
          {sale.shiftId !== null && sale.shiftMemberId !== null ? (
            <Link
              to="/vardiya/vardiyalar/$id/hesap/$memberId"
              params={{ id: sale.shiftId, memberId: sale.shiftMemberId }}
              className="underline underline-offset-4"
            >
              {t("pos.sales.openClosing")}
            </Link>
          ) : (
            "—"
          )}
        </Field>
        <Field label={t("pos.sales.fields.agent")}>
          {sale.agentId === null ? (
            "—"
          ) : (
            <Link to="/ajanlar/$id" params={{ id: sale.agentId }} className="underline underline-offset-4">
              {t("pos.sales.openAgent")}
            </Link>
          )}
        </Field>
        <Field label={t("pos.sales.fields.earchive")}>
          {sale.earchiveRequestId === null ? (
            "—"
          ) : (
            <Link to="/faturalar/e-arsiv-talepleri" className="underline underline-offset-4">
              {t("pos.sales.openEarchive")}
            </Link>
          )}
        </Field>
        {sale.voidOfSaleId ? (
          <Field label={t("pos.sales.fields.voidOf")}>
            <Link
              to="/kasa/satislar/$id"
              params={{ id: sale.voidOfSaleId }}
              className="underline underline-offset-4"
            >
              {t("pos.sales.openOriginal")}
            </Link>
          </Field>
        ) : null}
        {sale.voidedBySaleId ? (
          <Field label={t("pos.sales.fields.voidedBy")}>
            <Link
              to="/kasa/satislar/$id"
              params={{ id: sale.voidedBySaleId }}
              className="underline underline-offset-4"
            >
              {t("pos.sales.openVoid")}
            </Link>
          </Field>
        ) : null}
        <Field label={t("pos.sales.fields.receiptPrintedAt")}>{formatDateTime(sale.receiptPrintedAt)}</Field>
        {sale.vehiclePlate ? (
          <Field label={t("pos.sales.fields.vehiclePlate")}>{sale.vehiclePlate}</Field>
        ) : null}
        {sale.note ? <Field label={t("pos.sales.fields.note")}>{sale.note}</Field> : null}
      </dl>

      <dl className="grid gap-3 border-t pt-3 sm:grid-cols-4">
        <Field label={t("pos.sales.fields.gross")}>
          <MoneyText value={sale.gross} />
        </Field>
        <Field label={t("pos.sales.fields.discount")}>
          <MoneyText value={sale.discountTotal} />
        </Field>
        <Field label={t("pos.sales.fields.vat")}>
          <MoneyText value={sale.vatTotal} />
        </Field>
        <Field label={t("pos.sales.fields.net")}>
          <MoneyText value={sale.net} className="font-semibold" />
        </Field>
      </dl>
    </Card>
  );
}

function LinesCard({ sale }: { sale: PosSaleDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("pos.sales.linesTitle")}</h2>
      <SimpleTable
        caption={t("pos.sales.linesCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("pos.sales.fields.line")}
            </th>
            <th scope="col" className={thClass}>
              {t("pos.sales.fields.product")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.quantity")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.unitPrice")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.gross")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.discount")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.vat")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.net")}
            </th>
          </tr>
        }
      >
        {sale.lines.map((line) => (
          <tr key={line.lineNo} className="border-t align-top">
            <td className={`${tdClass} tabular`}>{line.lineNo}</td>
            <td className={tdClass}>
              <span className="grid gap-0.5">
                <span className="font-medium">{productText(line)}</span>
                <span className="text-xs text-muted-foreground">
                  {POS_LINE_KIND_LABELS[line.kind]}
                  {line.barcode ? ` · ${line.barcode}` : ""}
                  {line.isAgeRestricted ? ` · ${t("pos.sales.ageRestricted")}` : ""}
                </span>
                {line.fuelAgentTxId ? (
                  <span className="text-xs text-muted-foreground">
                    {t("pos.sales.fuelTxRef", { ref: line.fuelAgentTxId })}
                  </span>
                ) : null}
              </span>
            </td>
            <td className={tdRightClass}>{Decimal.from(line.quantity).formatTr(3)}</td>
            <td className={tdRightClass}>{formatUnitPrice(line.unitPrice)}</td>
            <td className={tdRightClass}>
              <MoneyText value={line.gross} />
            </td>
            <td className={tdRightClass}>
              <span className="grid justify-items-end gap-0.5">
                <MoneyText value={line.discountAmount} />
                {(line.discountCampaignName ?? line.discountReason) ? (
                  <span className="text-xs text-muted-foreground">
                    {line.discountCampaignName ?? line.discountReason}
                  </span>
                ) : null}
              </span>
            </td>
            <td className={tdRightClass}>
              <span className="grid justify-items-end gap-0.5">
                <MoneyText value={line.vatAmount} />
                <span className="text-xs text-muted-foreground">%{line.vatRatePercent}</span>
              </span>
            </td>
            <td className={tdRightClass}>
              <MoneyText value={line.net} className="font-medium" />
            </td>
          </tr>
        ))}
      </SimpleTable>

      {sale.vatBreakdown.length > 0 ? (
        <SimpleTable
          caption={t("pos.sales.vatCaption")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("pos.sales.fields.vatRate")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("pos.sales.fields.vatBase")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("pos.sales.fields.vat")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("pos.sales.fields.gross")}
              </th>
            </tr>
          }
        >
          {sale.vatBreakdown.map((row) => (
            <tr key={row.vatRateCode} className="border-t">
              <td className={tdClass}>%{row.ratePercent}</td>
              <td className={tdRightClass}>
                <MoneyText value={row.base} />
              </td>
              <td className={tdRightClass}>
                <MoneyText value={row.vat} />
              </td>
              <td className={tdRightClass}>
                <MoneyText value={row.gross} />
              </td>
            </tr>
          ))}
        </SimpleTable>
      ) : null}
    </Card>
  );
}

function productText(line: PosSaleLine): string {
  return line.productName ?? line.description ?? line.productCode ?? "—";
}

const CREDIT_VERDICT_TONE: Record<string, Tone> = {
  ALLOWED: "success",
  TOLERANCE: "warning",
  BLOCKED: "danger",
  LIMIT_EXCEEDED: "danger",
};

function TendersCard({ sale }: { sale: PosSaleDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("pos.sales.tendersTitle")}</h2>
      <p className="text-sm text-muted-foreground">{t("pos.sales.tendersHint")}</p>
      <SimpleTable
        caption={t("pos.sales.tendersCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("pos.sales.fields.tenderType")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.amount")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("pos.sales.fields.changeGiven")}
            </th>
            <th scope="col" className={thClass}>
              {t("pos.sales.fields.tenderDetail")}
            </th>
          </tr>
        }
      >
        {sale.tenders.map((tender) => (
          <tr
            key={tender.tenderNo}
            className={`border-t align-top ${tender.supersededAt ? "text-muted-foreground line-through" : ""}`}
          >
            <td className={tdClass}>
              <span className="grid gap-0.5">
                <span className="font-medium">{tenderLabel(tender.tenderType)}</span>
                {tender.supersededAt ? (
                  // Üstü çizili satır ekran okuyucuda da anlaşılmalı: gerekçe METİN olarak yazılır.
                  <span className="text-xs no-underline">
                    {t("pos.sales.superseded", {
                      at: formatDateTime(tender.supersededAt),
                      reason: tender.supersededReason ?? "—",
                    })}
                  </span>
                ) : null}
              </span>
            </td>
            <td className={tdRightClass}>
              <MoneyText value={tender.amount} />
            </td>
            <td className={tdRightClass}>
              <MoneyText value={tender.changeGiven} />
            </td>
            <td className={tdClass}>
              <span className="grid gap-0.5 text-xs">
                {tender.posTerminalName ? <span>{tender.posTerminalName}</span> : null}
                {tender.slipNo ? <span className="font-mono">{tender.slipNo}</span> : null}
                {tender.counterpartyName ? <span>{tender.counterpartyName}</span> : null}
                {tender.creditCheck ? <CreditCheck check={tender.creditCheck} /> : null}
              </span>
            </td>
          </tr>
        ))}
      </SimpleTable>
    </Card>
  );
}

function CreditCheck({ check }: { check: NonNullable<PosSaleTender["creditCheck"]> }): ReactElement {
  const { t } = useTranslation();
  const verdict = check.verdict ?? "—";
  return (
    <span className="grid gap-0.5 no-underline">
      <StatusBadge tone={CREDIT_VERDICT_TONE[verdict] ?? "neutral"}>
        {t(`pos.sales.creditVerdict.${verdict}`, { defaultValue: verdict })}
      </StatusBadge>
      <span>
        {t("pos.sales.creditAvailable", {
          before: formatMinor(check.availableBefore),
          after: formatMinor(check.availableAfter),
        })}
      </span>
      {check.overridden ? <span>{t("pos.sales.creditOverridden")}</span> : null}
    </span>
  );
}

function WarningsCard({ sale }: { sale: PosSaleDetail }): ReactElement | null {
  const { t } = useTranslation();
  if (sale.warnings.length === 0) return null;
  return (
    <Card className="grid gap-2 p-4">
      <h2 className="text-base font-semibold">{t("pos.sales.warningsTitle")}</h2>
      <p className="text-sm text-muted-foreground">{t("pos.sales.warningsHint")}</p>
      <ul className="list-inside list-disc text-sm" data-testid="pos-sale-warnings">
        {sale.warnings.map((warning) => (
          <li key={warning}>{warningLabel(warning)}</li>
        ))}
      </ul>
    </Card>
  );
}
